import * as React          from 'react';
import { Helmet }          from 'react-helmet';
import { useTranslation }  from 'react-i18next';
import { SubmissionError } from 'redux-form';

import { hasAccessOnly } from '../../../../../common/services/accessChecker';

import RenderButton from '../../../../components/buttons/base/RenderButton';
import Preloader    from '../../../../components/containers/preloader';
import PageHeader   from '../../../../components/pageHeader';
import { useUser }  from '../../../../providers/user';

import ChangeAccountData from './ChangeAccountData';
import ChangeProfileData from './ChangeProfileData';
import ProfileView       from './ProfileView';

const Profile = function() {
  const { t } = useTranslation();

  const user = useUser();

  const [action, setAction] = React.useState('view');

  const cancelEditMode          = React.useCallback(() => {
    setAction('view');
  }, []);
  const handleSubmitAccountData = React.useCallback((event, handleSubmit) => {
    event.preventDefault();

    handleSubmit(values => {
      return user.submitAccountData(values)
        .then(cancelEditMode)
        .catch(errors => {
          throw new SubmissionError(errors);
        });
    })(event);
  }, [cancelEditMode]);
  const handleSubmitUserData    = React.useCallback((event, handleSubmit) => {
    event.preventDefault();

    handleSubmit((values) => {
      return user.submitUserData(values)
        .then(cancelEditMode)
        .catch(errors => {
          throw new SubmissionError(errors);
        });
    })(event);
  }, [cancelEditMode]);

  const accountData = React.useMemo(() => {
    const props = {
      cancel:     cancelEditMode,
      submitForm: handleSubmitAccountData,
    };

    if (user.profileInitialStateLoaded) {
      props.initialValues = {
        _token:   user.form._token,
        formName: 'accountData',
      };
    }

    return props;
  }, [cancelEditMode, handleSubmitAccountData, user.form, user.profileInitialStateLoaded]);
  const meta        = React.useMemo(() => {
    return {
      title:       `${t('client_userAccount')} - ${t('steelline')}`,
      keywords:    t('client_userAccount'),
      description: t('client_userAccount'),
    };
  }, []);
  const userData    = React.useMemo(() => {
    const props = {
      cancel:     cancelEditMode,
      email:      user.form.email,
      submitForm: handleSubmitUserData,
    };

    if (user.profileInitialStateLoaded) {
      props.initialValues = {
        _token:     user.form._token,
        address:    user.form.address,
        firstName:  user.form.firstName,
        formName:   'userData',
        lastName:   user.form.lastName,
        login:      user.form.login,
        patronymic: user.form.patronymic,
        phone:      user.form.phone,
      };
    }

    return props;
  }, [cancelEditMode, handleSubmitUserData, user.form, user.profileInitialStateLoaded]);

  React.useEffect(() => {
    if (!user.profileInitialStateLoaded && !user.loading) {
      user.getProfileData();
    }

    scrollTo({
      left: 0,
      top:  0,
    });

    return () => {
      if (action !== 'view') {
        setAction('view');
      }
    };
  }, []);

  return (
    <>
      <div className={'page-content'}>
        <Helmet>
          <title>{meta.title}</title>
          <meta
            content={meta.description}
            name={'description'}
          />
          <meta
            content={meta.keywords}
            name={'keywords'}
          />
        </Helmet>
        <PageHeader>{t('client_userAccount')}</PageHeader>
        <main className={'profile'}>
          {action !== 'changeData'
            ? <ProfileView {...user.form} />
            : <ChangeProfileData {...userData} />
          }
          {action === 'changePassword' && <ChangeAccountData {...accountData} />}
          <section className={'to-catalog'}>
            <div className={'container'}>
              {action === 'view' && !hasAccessOnly.calculatorUser() && (
                <>
                  <RenderButton
                    action={() => {
                      setAction('changeData');
                    }}
                    className={'fill'}
                    label={t('client_changeInformation')}
                  />
                  <RenderButton
                    action={() => {
                      setAction('changePassword');
                    }}
                    label={t('client_changePassword')}
                  />
                </>
              )}
              <RenderButton
                action={user.userDelete}
                label={t('client_removeAccount')}
              />
            </div>
          </section>
        </main>
      </div>
      {(user.loading || user.wait) && <Preloader />}
    </>
  );
};
export default Profile;